import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Components
import LoginPage from './components/LoginPage';
import MerchantSelect from './components/MerchantSelect';
import MerchantLanding from './components/MerchantLanding';
import Categories from './components/Categories';
import Products from './components/Products';
import LandingRedirectMeli from './components/LandingRedirectMeli';
import ShopifyLanding from './components/ShopifyLanding';
import ShopifyInstall from './components/ShopifyInstall';
import ShopifyOauth from './components/ShopifyOauth';
import ShopifyApp from './components/ShopifyApp';
import ShopifyProducts from './components/products/ShopifyProducts';
import MeliProducts from './components/products/MeliProducts';
import GlobalShopOrders from './components/GlobalShopOrders';
import MerchantOrders from './components/MerchantOrders';
import Settings from './components/shopify/Settings';
import CategoriesSettings from './components/shopify/settings/Categories';

// Session Context
const SessionContext = createContext();

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};

// Session Provider Component
const SessionProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userType, setUserType] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check authentication on app load
    useEffect(() => {
        const authToken = localStorage.getItem('authToken');
        const storedUserType = localStorage.getItem('userType');

        if (authToken && storedUserType) {
            setIsAuthenticated(true);
            setUserType(storedUserType);
        }

        // Mark loading as complete
        setIsLoading(false);
    }, []);

    const login = () => {
        setIsAuthenticated(true);
        const storedUserType = localStorage.getItem('userType');
        setUserType(storedUserType);
    };

    const logout = () => {
        setIsAuthenticated(false);
        setUserType(null);
    };

    return (
        <SessionContext.Provider value={{ isAuthenticated, userType, isLoading, login, logout }}>
            {children}
        </SessionContext.Provider>
    );
};

// Private Route Component
const PrivateRoute = ({ children, allowedUserTypes = null }) => {
    const { isAuthenticated, userType, isLoading } = useSession();

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Checking authentication...</p>
                </div>
            </div>
        );
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
        return <Navigate to="/index" replace />;
    }

    // Check if user type is allowed (if specified)
    if (allowedUserTypes && !allowedUserTypes.includes(userType)) {
        return <Navigate to="/index" replace />;
    }

    return children;
};

// App Wrapper Component
const AppWrapper = () => {
    const { isLoading } = useSession();

    // Show loading while checking authentication
    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Initializing application...</p>
                </div>
            </div>
        );
    }

    return (
        <Router>
            <div className="App">
                <Routes>
                    {/* Public Routes */}
                    <Route path="/shopify" element={<ShopifyLanding />} />
                    <Route path="/shopify/app" element={<ShopifyInstall />} />
                    <Route path="/shopify/api/auth" element={<ShopifyOauth />} />
                    <Route path="/shopify/dashboard" element={<ShopifyApp />} />
                    <Route path="/shopify/:shop/dashboard" element={<ShopifyApp />} />
                    <Route path="/shopify/:shop/products" element={<ShopifyProducts />} />
                    <Route path="/shopify/:shop/meli-products" element={<MeliProducts />} />
                    <Route path="/landing" element={<LandingRedirectMeli />} />
                    <Route path="/index" element={<LoginPage />} />
                    <Route path="/" element={<Navigate to="/index" replace />} />

                    {/* Admin Private Routes */}
                    <Route
                        path="/private/*"
                        element={
                            <PrivateRoute allowedUserTypes={['admin']}>
                                <Routes>
                                    <Route path="/index" element={<MerchantSelect />} />
                                    <Route path="/:merchant" element={<MerchantLanding />} />
                                    <Route path="/:merchant/categories" element={<Categories />} />
                                    <Route path="/:merchant/products" element={<Products />} />
                                    <Route path="/:merchant/orders" element={<MerchantOrders />} />
                                    <Route path="/:merchant/orders/:orderId" element={<MerchantOrders />} />
                                </Routes>
                            </PrivateRoute>
                        }
                    />

                    {/* Global Shop Private Routes */}
                    <Route
                        path="/privategx/*"
                        element={
                            <PrivateRoute allowedUserTypes={['globalshopex']}>
                                <Routes>
                                    <Route path="/orders" element={<GlobalShopOrders />} />
                                </Routes>
                            </PrivateRoute>
                        }
                    />

                    {/* Settings Routes */}
                    <Route path="/shopify/:shop/settings" element={<Settings />} />
                    <Route path="/shopify/:shop/settings/categories" element={<CategoriesSettings />} />

                    {/* Catch all - redirect to login */}
                    <Route path="*" element={<Navigate to="/index" replace />} />
                </Routes>
            </div>
        </Router>
    );
};

function App() {
    return (
        <SessionProvider>
            <AppWrapper />
        </SessionProvider>
    );
}

export default App;
